import { DatePipe, NgClass, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RegistrationService } from '@core/http/registration.service';
import {
  ColegiadoValidationResponse,
  RegistroAssetPayload,
  SolicitudRegistro,
  SolicitudRegistroPayload,
} from '@core/models/Registration';
import { environment } from '@enviroment/environment';
import Swal from 'sweetalert2';

export interface RegistrationRequestDetailDialogData {
  request: SolicitudRegistro;
  payload: SolicitudRegistroPayload | null;
}

@Component({
  selector: 'app-registration-request-detail-dialog',
  standalone: true,
  imports: [NgIf, NgClass, DatePipe, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './registration-request-detail-dialog.component.html',
  styleUrl: './registration-request-detail-dialog.component.scss',
})
export class RegistrationRequestDetailDialogComponent {
  validatingColegiado = false;
  colegiadoValidation: ColegiadoValidationResponse | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RegistrationRequestDetailDialogData,
    private registrationService: RegistrationService,
  ) {}

  get imagePreviewUrl(): string | null {
    return this.toAssetUrl(this.data.payload?.imagen);
  }

  get signaturePreviewUrl(): string | null {
    return this.toAssetUrl(this.data.payload?.firma);
  }

  get canValidateColegiado(): boolean {
    const tipoColegiado = Number(this.data.payload?.medico?.colegiado ?? 0);
    const numeroColegiado = String(this.data.payload?.medico?.cedulaGeneral ?? '').trim();
    return this.isSpainRegion && [1, 2, 3].includes(tipoColegiado) && !!numeroColegiado;
  }

  get isSpainRegion(): boolean {
    return String(environment.region ?? '').trim().toUpperCase() === 'ES';
  }

  get colegiadoName(): string {
    const tipoColegiado = Number(this.data.payload?.medico?.colegiado ?? 0);
    if (tipoColegiado === 1) return 'COLEGIO DE MEDICOS';
    if (tipoColegiado === 2) return 'COLEGIO DE DENTISTAS';
    if (tipoColegiado === 3) return 'COLEGIO DE PODOLOGOS';
    return 'NO DEFINIDO';
  }

  get colegiadoStatusClass(): string {
    if (!this.colegiadoValidation) return '';
    return this.colegiadoValidation.habilitado ? 'approved' : 'rejected';
  }

  validateColegiado(): void {
    if (!this.canValidateColegiado) {
      Swal.fire('VALIDACION NO DISPONIBLE', 'NO HAY DATOS SUFICIENTES PARA VALIDAR EL COLEGIADO.', 'warning').then();
      return;
    }

    const tipoColegiado = Number(this.data.payload?.medico?.colegiado ?? 0);
    const numeroColegiado = String(this.data.payload?.medico?.cedulaGeneral ?? '').trim();

    this.validatingColegiado = true;
    this.colegiadoValidation = null;

    this.registrationService.validateColegiado(tipoColegiado, numeroColegiado).subscribe({
      next: (response) => {
        this.validatingColegiado = false;
        this.colegiadoValidation = response;

        if (response.habilitado) {
          Swal.fire(
            'COLEGIADO VALIDADO',
            `EL MEDICO APARECE HABILITADO EN ${this.colegiadoName}.`,
            'success'
          ).then();
          return;
        }

        Swal.fire(
          'MEDICO NO HABILITADO',
          `EL MEDICO NO ESTA HABILITADO EN EL ${this.colegiadoName} DESIGNADO.`,
          'warning'
        ).then();
      },
      error: (error) => {
        this.validatingColegiado = false;
        this.colegiadoValidation = null;

        const message =
          error?.error?.message ||
          error?.error?.descripcion?.[0] ||
          'NO SE PUDO VALIDAR EL COLEGIADO.';

        Swal.fire('VALIDACION FALLIDA', message, 'error').then();
      },
    });
  }

  private toAssetUrl(asset?: RegistroAssetPayload | null): string | null {
    if (!asset?.blobBase64) return null;
    const contentType = asset.contentType || 'image/png';
    return `data:${contentType};base64,${asset.blobBase64}`;
  }
}
